import React, { useState, useEffect, FC } from 'react';
import { Tab, useTranslate } from 'react-admin';
import { PaperTabbedShowLayout, Show } from '~/layout';
import DetailsItem from './components/DetailsItem';
import DetailsMenu from './components/DetailsMenu';
import CategoryIcon from '@material-ui/icons/Category';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import TreeView from '@material-ui/lab/TreeView';
import { Grid, makeStyles } from '@material-ui/core';
import { NoPermissions } from '~/components/ra';
import { useShowController } from 'react-admin';
import { Menu, MenuItem, TreeNode } from '~/types/Menu';
import { useFetchInitialChildren, useFetchChildrenById } from '~/hooks/menu';
import theme from '~/theme';
import {
  transformMenuItem,
  getDescendantIds,
  findNodeById,
  initializeTreeData,
  isNodeExpandable,
} from '~/utils/menu';
import { LoadingTreeItem } from './components/LoadingTreeItem';
import { useNotify } from '~/hooks';

const l = (key: string): string => `resources.menu.show.${key}`;

const MenuShow: FC<any> = (props) => {
  const { permissions } = props;
  const showController = useShowController(props);
  const menuRecord: Menu = showController?.record;

  const notify = useNotify();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`${key}`));
  const classes = useStyles();

  const [selectedType, setSelectedType] = useState<'menu' | 'action' | 'service' | 'group'>('menu');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(menuRecord?.id);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  const { data: children, isLoading: isChildrenLoading } = useFetchInitialChildren(menuRecord?.id);
  useEffect(() => {
    if (menuRecord) {
      setTreeData((prevTreeData) => {
        const initialChildren = children?.map((child: MenuItem) => {
          let initialMenuItemChildren: TreeNode[] | undefined =
            child?.itemType === 'GROUP' ? [] : undefined;
          if (child?.itemType === 'GROUP') {
            const existingNode = findNodeById(prevTreeData, child.id);
            if (existingNode?.children?.length) {
              initialMenuItemChildren = existingNode.children;
            }
          }
          return transformMenuItem(
            child,
            initialMenuItemChildren,
            menuRecord?.id,
            isChildrenLoading
          );
        });
        const initialTreeData = initializeTreeData(
          menuRecord,
          initialChildren,
          isChildrenLoading
        ).map((node: TreeNode) => ({
          ...node,
          children: node.children
            ? node.children?.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
            : null,
        }));
        setTreeData(initialTreeData as any);

        return initialTreeData;
      });
      setExpandedNodes((prev) => (prev.length ? [...prev, menuRecord?.id] : [menuRecord?.id]));
    }
  }, [children, isChildrenLoading, menuRecord]);

  const { mutate: fetchChildrenById } = useFetchChildrenById();

  const renderTree = (nodes: TreeNode[]) =>
    nodes?.map((node) => {
      const handleLabelClick = (event: any) => {
        event.preventDefault();
        setSelectedType(node?.type);
        setSelectedNodeId(node.id);
      };
      const isExpandable = isNodeExpandable(node);
      return (
        <LoadingTreeItem
          key={node.id}
          nodeId={node.id}
          label={node?.isLoading ? t('loading') : node?.name}
          isLoading={node?.isLoading}
          onClick={handleLabelClick}
        >
          {isExpandable && (node?.children?.length ? renderTree(node.children) : <div />)}
        </LoadingTreeItem>
      );
    });

  const handleNodeToggle = async (_event: React.ChangeEvent<{}>, nodeIds: string[]) => {
    const newlyExpandedNode = nodeIds.find((id) => !expandedNodes.includes(id));
    setExpandedNodes(nodeIds);
    if (newlyExpandedNode) {
      const node = findNodeById(treeData, newlyExpandedNode);
      if (node && node?.id !== menuRecord?.id && !node.isLoading) {
        await loadChildren(node.id);
      }
    }
  };

  const loadChildren = async (nodeId: string, forceReload?: boolean) => {
    const updatedTreeData = [...treeData];
    const node = findNodeById(updatedTreeData, nodeId);
    if (node) {
      if (!forceReload && node.children && node.children.length > 0) {
        return;
      }
      node.isLoading = true;
      const descendantIds = getDescendantIds(node);
      setExpandedNodes((prevExpandedNodes) =>
        prevExpandedNodes.filter((id) => !descendantIds.includes(id))
      );
      fetchChildrenById(
        { menuId: menuRecord?.id!, itemId: nodeId },
        {
          onSuccess: (fetchedChildren) => {
            const transformedChildren = fetchedChildren
              .map((child: MenuItem) => {
                return transformMenuItem(
                  child,
                  child?.itemType === 'GROUP' ? [] : undefined,
                  node?.id,
                  false
                );
              })
              .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
            node.children = transformedChildren;
            node.isLoading = false;
            setTreeData([...updatedTreeData]);
            if (selectedNodeId) {
              const updatedNode = findNodeById(updatedTreeData, selectedNodeId);
              if (!updatedNode) {
                setSelectedNodeId(nodeId);
              }
            }
          },
          onError: (error: any) => {
            notify({
              type: 'error',
              message: error?.message
                ? `${t('loadChildrenError')}: ${error?.message}`
                : t('loadChildrenError'),
            });
            node.isLoading = false;
            setTreeData([...updatedTreeData]);
          },
        }
      );
    }
  };

  const selectedNode = selectedNodeId ? findNodeById(treeData, selectedNodeId) : null;

  if (!permissions) return null;
  if (!menuRecord) return null;
  if (!permissions?.includes('dynamicMenu.view')) return <NoPermissions />;
  return (
    <Show {...props} actions={null}>
      <PaperTabbedShowLayout>
        <Tab label={t('tabs.details')}>
          <Grid container xs={12}>
            <Grid item xs={3} style={{ borderRight: `1px solid ${theme.palette.divider}` }}>
              <TreeView
                className={classes.root}
                defaultCollapseIcon={<RemoveIcon className={classes.actionButtons} />}
                defaultExpandIcon={<AddIcon className={classes.actionButtons} />}
                defaultEndIcon={<CategoryIcon className={classes.actionButtons} />}
                expanded={expandedNodes}
                selected={[selectedNodeId!]}
                onNodeToggle={handleNodeToggle}
              >
                {renderTree(treeData)}
              </TreeView>
            </Grid>
            <Grid item xs={9}>
              {selectedType === 'menu' ? (
                <DetailsMenu selectedNode={selectedNode} />
              ) : (
                <DetailsItem
                  selectedNode={selectedNode}
                  menuId={menuRecord?.id}
                  loadChildren={loadChildren}
                />
              )}
            </Grid>
          </Grid>
        </Tab>
      </PaperTabbedShowLayout>
    </Show>
  );
};

const useStyles = makeStyles({
  root: {
    height: '70vh',
    flexGrow: 1,
    overflowX: 'scroll',
  },
  actionButtons: {
    color: theme.palette.text.primary,
    opacity: 0.5,
    width: 15,
    height: 15,
  },
});

export default MenuShow;
